import {
	LimitScope,
	PlanEntitlementAlias,
	SideUsageAlias,
	TrialKind
} from '@brand-lolly/app/billing/types/billing.enums';
import { planRowLabel } from '@brand-lolly/app/billing/types/alias-labels';
import { CatalogPlan } from '@brand-lolly/app/billing/types/plan.types';

import { PlanCardRow } from './plan-card.component';

/**
 * Maps a catalog plan's limits / entitlements onto the label/value rows
 * rendered by `PlanCardComponent`, and onto the full grouped list behind its
 * "See everything included" link. Shared by the pricing page, the manage-plan
 * settings page, the Social Listening onboarding page and the admin-panel plans
 * feature — one mapping, four surfaces.
 *
 * EVERY ROW IS GENERATED FROM A REAL ENTITLEMENT, on both surfaces. Nothing
 * here states a capability the plan does not carry, or a number the catalogue
 * does not hold: the card picks WHICH of a plan's rows to show, never what they
 * say. That is the property that keeps these pages from drifting away from the
 * seed, and it survives the trimming below unchanged.
 */

/** Scope whose rows a card shows: app-trial plans show their trial grant. */
function planScope(plan: CatalogPlan): LimitScope {
	return plan.trial_kind === TrialKind.APP
		? LimitScope.APP_TRIAL
		: LimitScope.PAID;
}

function entitlementValue(cap: number | null, enabled: boolean | null): string {
	if (enabled !== null) return enabled ? 'On' : 'Off';
	if (cap === null) return 'Unlimited';
	return String(cap);
}

/**
 * THE CARD'S WHOLE LIST, and the reason each line is on it.
 *
 * A pricing card answers one question — *which one do I buy* — and that is
 * settled by what a tier opens and by the size of the work it lets you do. It
 * is NOT settled by a ceiling: "50 keywords per brand" answers *am I about to
 * run out*, which is a question for someone who already holds the plan, and
 * that reader is served by the billing page's usage section (with the real
 * used/cap beside it, which a pricing card can never show) and by the full list
 * behind the card's own link.
 *
 * The catalogue carries 24 entitlements on every application plan, so the card
 * used to print 24 rows — three of which said the same thing on every tier
 * (`feature.ai_messaging` and `feature.discovery` are On everywhere,
 * `feature.data_updates_realtime` is Off everywhere, i.e. a feature nobody can
 * buy), and twelve of which were ceilings. A row that reads identically on
 * every card cannot help anyone choose between them; that is the rule that
 * removes the first three, and it is why this list is a list of aliases rather
 * than a count.
 *
 * Deliberately NOT here, beyond the ceilings:
 *   - `app_credits_grant_per_month` — the dial above the button IS the
 *     allowance, and the row underneath restated the same figure.
 *   - `feature.advanced_filters`, `feature.sentiment_analysis`,
 *     `feature.data_updates_hourly` — Social Listening's own capabilities,
 *     which switch on exactly when Social Listening does. The row above already
 *     says them.
 *   - `feature.ad_insights`, `feature.ai_content_scoring` — the same relation to
 *     Partner Ads.
 */
const CARD_ROW_ALIASES: readonly string[] = [
	// The one product a tier withholds. The largest single step in the
	// catalogue, and the thing a visitor most often came to check.
	PlanEntitlementAlias.FEATURE_SOCIAL_LISTENING,
	// The second product-level gate.
	PlanEntitlementAlias.FEATURE_PARTNER_ADS,
	// How many searches you may keep …
	PlanEntitlementAlias.MAX_PROJECTS_CREATED,
	// … and how large one may get. Together these are the scale of the work
	// itself, which is the one numeric thing a buyer can weigh before buying.
	PlanEntitlementAlias.MAX_INFLUENCERS_PER_PROJECT,
	// How many of us it seats — the third axis, and the only row here about
	// people rather than about the product. Last because it is the question a
	// buyer reaches only once the two above have settled which tier they want.
	PlanEntitlementAlias.MAX_SEATS
];

const CARD_ROW_ORDER = new Map(
	CARD_ROW_ALIASES.map((alias, index) => [alias, index])
);

/**
 * The full list, grouped the way the backend's own alias file groups them
 * (`app/billing/enforcer/aliases.py`): by the product that owns the alias. The
 * order is the order the dialog prints.
 *
 * An alias missing from every group is not dropped — it falls into the trailing
 * "Also included" group, so a newly seeded entitlement appears on the detail
 * surface the day it is seeded, without a frontend change.
 */
interface PlanDetailGroupSpec {
	title: string;
	aliases: readonly string[];
}

const DETAIL_GROUPS: readonly PlanDetailGroupSpec[] = [
	{
		title: 'Credits',
		aliases: [
			PlanEntitlementAlias.APP_CREDITS_GRANT_PER_MONTH,
			PlanEntitlementAlias.API_CREDITS_GRANT_PER_MONTH
		]
	},
	{
		title: 'Influencer discovery',
		aliases: [
			PlanEntitlementAlias.FEATURE_DISCOVERY,
			PlanEntitlementAlias.MAX_PROJECTS_CREATED,
			PlanEntitlementAlias.MAX_INFLUENCERS_PER_PROJECT
		]
	},
	{
		title: 'Outreach',
		// Both spellings of the SidePlan caps: the prefixed form is what the
		// plan carries as its own entitlement, the bare form is what the
		// projection serves for a plan that states none of its own.
		aliases: [
			PlanEntitlementAlias.FEATURE_AI_MESSAGING,
			PlanEntitlementAlias.SIDE_EMAIL_ACCOUNTS,
			SideUsageAlias.EMAIL_ACCOUNTS,
			PlanEntitlementAlias.SIDE_INSTAGRAM_ACCOUNTS,
			SideUsageAlias.INSTAGRAM_ACCOUNTS,
			PlanEntitlementAlias.SIDE_TIKTOK_ACCOUNTS,
			SideUsageAlias.TIKTOK_ACCOUNTS,
			SideUsageAlias.MESSAGES_PER_MONTH,
			SideUsageAlias.EMAIL_UNLOCKS
		]
	},
	{
		title: 'Partner Ads',
		aliases: [
			PlanEntitlementAlias.FEATURE_PARTNER_ADS,
			PlanEntitlementAlias.FEATURE_AD_INSIGHTS,
			PlanEntitlementAlias.FEATURE_AI_CONTENT_SCORING,
			PlanEntitlementAlias.MAX_AD_ACCOUNTS,
			PlanEntitlementAlias.MAX_INSTAGRAM_ACCOUNTS,
			PlanEntitlementAlias.MAX_FACEBOOK_ACCOUNTS,
			PlanEntitlementAlias.MAX_TIKTOK_ACCOUNTS,
			PlanEntitlementAlias.MAX_X_ACCOUNTS
		]
	},
	{
		title: 'Social Listening',
		aliases: [
			PlanEntitlementAlias.FEATURE_SOCIAL_LISTENING,
			PlanEntitlementAlias.FEATURE_SENTIMENT_ANALYSIS,
			PlanEntitlementAlias.FEATURE_ADVANCED_FILTERS,
			PlanEntitlementAlias.FEATURE_ALL_MENTIONS,
			PlanEntitlementAlias.FEATURE_DATA_UPDATES_HOURLY,
			PlanEntitlementAlias.FEATURE_DATA_UPDATES_REALTIME,
			PlanEntitlementAlias.MAX_TRACKED_BRANDS,
			PlanEntitlementAlias.MAX_COMPETITOR_PROJECTS,
			PlanEntitlementAlias.MAX_TRACKED_COMPETITORS,
			PlanEntitlementAlias.MAX_INDUSTRY_MONITORS,
			PlanEntitlementAlias.MAX_KEYWORDS_PER_BRAND,
			PlanEntitlementAlias.MAX_CONNECTED_SOCIAL_ACCOUNTS
		]
	}
];

/** Where an alias the groups do not name ends up. */
const DETAIL_FALLBACK_GROUP = 'Also included';

/** One titled block of the full list. */
export interface PlanDetailGroup {
	title: string;
	rows: PlanCardRow[];
}

/** A plan row before it is grouped — the alias is what the grouping reads. */
interface AliasedRow extends PlanCardRow {
	alias: string;
}

/** Every row a plan carries, in the catalogue's own order. */
function allRows(plan: CatalogPlan): AliasedRow[] {
	const scope = planScope(plan);
	const rows: AliasedRow[] = [];
	// Entitlements first (the backend already orders them: feature toggles,
	// single-value caps, then usage-counted caps), then the renewable limits.
	for (const entitlement of plan.entitlements ?? []) {
		if (entitlement.scope !== scope) continue;
		rows.push({
			alias: entitlement.alias,
			label: planRowLabel(entitlement.alias, entitlement.label),
			value: entitlementValue(
				entitlement.cap ?? null,
				entitlement.enabled ?? null
			)
		});
	}
	for (const limit of plan.limits ?? []) {
		if (limit.scope !== scope) continue;
		rows.push({
			alias: limit.limit_alias,
			label: planRowLabel(limit.limit_alias, limit.label),
			value: limit.value === null ? 'Unlimited' : String(limit.value)
		});
	}
	return rows;
}

/**
 * The SidePlan projection, minus anything the plan already states itself.
 *
 * The backend projects the cross-product SidePlan allowances onto every plan
 * (keyed on `tier_level`, trial-kind-agnostic) AND the application plans carry
 * their own `side.*` entitlements — the same three mailbox caps, arriving
 * twice, and on the free tier the two do not agree (the projection says 3 email
 * mailboxes where the plan's own entitlement says 1). Printing both put two
 * different numbers for one thing on one card. The plan's own entitlement wins
 * because it is the row this plan actually carries; the projection is kept only
 * for what the plan says nothing about, which on every application plan is the
 * monthly message allowance.
 */
function projectedSideRows(plan: CatalogPlan, own: AliasedRow[]): AliasedRow[] {
	const stated = new Set(own.map(row => row.alias.replace(/^side\./, '')));
	const rows: AliasedRow[] = [];
	for (const limit of plan.side_limits ?? []) {
		if (stated.has(limit.alias)) continue;
		rows.push({
			alias: limit.alias,
			label: planRowLabel(limit.alias, limit.label),
			value: limit.value === null ? 'Unlimited' : String(limit.value)
		});
	}
	for (const entitlement of plan.side_entitlements ?? []) {
		if (stated.has(entitlement.alias)) continue;
		rows.push({
			alias: entitlement.alias,
			label: planRowLabel(entitlement.alias, entitlement.label),
			value: entitlementValue(
				entitlement.cap ?? null,
				entitlement.enabled ?? null
			)
		});
	}
	return rows;
}

/**
 * The rows the CARD prints: {@link CARD_ROW_ALIASES}, in that order.
 *
 * A plan the list does not describe at all keeps every row it has. That is the
 * retired per-product plans a grandfathered brand is still shown on the
 * manage-plan page — they speak a different vocabulary (`mentions_per_month`,
 * `max_social_listening_projects`), carry few rows to begin with, and a card
 * with an empty body would tell such a brand nothing about the plan it is
 * paying for.
 */
export function buildFeatureRows(plan: CatalogPlan): PlanCardRow[] {
	const rows = allRows(plan);
	const chosen = rows
		.filter(row => CARD_ROW_ORDER.has(row.alias))
		.sort(
			(a, b) =>
				(CARD_ROW_ORDER.get(a.alias) ?? 0) -
				(CARD_ROW_ORDER.get(b.alias) ?? 0)
		);
	const shown = chosen.length > 0 ? chosen : rows;
	return shown.map(({ label, value }) => ({ label, value }));
}

/**
 * The full list behind the card's "See everything included" link: everything
 * the plan carries, grouped by the product that owns it.
 */
export function buildPlanDetailGroups(plan: CatalogPlan): PlanDetailGroup[] {
	const own = allRows(plan);
	const rows = [...own, ...projectedSideRows(plan, own)];
	const taken = new Set<string>();
	const groups: PlanDetailGroup[] = [];

	for (const spec of DETAIL_GROUPS) {
		const inGroup: PlanCardRow[] = [];
		for (const alias of spec.aliases) {
			const row = rows.find(
				candidate => candidate.alias === alias && !taken.has(alias)
			);
			if (!row) continue;
			taken.add(alias);
			inGroup.push({ label: row.label, value: row.value });
		}
		if (inGroup.length > 0) {
			groups.push({ title: spec.title, rows: inGroup });
		}
	}

	const rest = rows
		.filter(row => !taken.has(row.alias))
		.map(({ label, value }) => ({ label, value }));
	if (rest.length > 0) {
		groups.push({ title: DETAIL_FALLBACK_GROUP, rows: rest });
	}
	return groups;
}
